import {env} from '@src/config';
import {
  ErrorContainer,
  ErrorText,
  ErrorTriangle,
  ExtraText,
  FieldLabel,
} from '@src/features/auth/components/AuthInput';
import {languageService} from '@src/features/my-profile/logic/language-service';
import Autocomplete from 'react-google-autocomplete';
import styled, {CSSProperties, useTheme} from 'styled-components';

export interface Place {
  address: string;
  zipCode?: string;
  city?: string;
  country: string;
  latitude: string;
  longitude: string;
  countryShortName: string;
}
type GSearchProps = {
  inputType: 'primary' | 'secondary';
  label?: string;
  error?: string;
  value?: Place;
  type: 'address' | 'city';
  handleNewPlace: (place: Place) => void;
  onResetFilter?: () => void;
  customStyle?: CSSProperties;
  customContainerStyle?: CSSProperties;
  placeHolder?: string;
};
export const AutocompleteContainer = styled.div`
  position: relative;
`;
export const Label = FieldLabel;
export const GSearch = ({
  handleNewPlace,
  label,
  value,
  error,
  customStyle,
  customContainerStyle,
  placeHolder,
  onResetFilter,
  type,
  ...props
}: GSearchProps) => {
  const {colors} = useTheme();
  const checkIfValueEmpty = (e: string) => {
    if (e === '' && onResetFilter) {
      onResetFilter();
    }
  };
  return (
    <AutocompleteContainer style={customContainerStyle}>
      {label ? (
        <Label>
          <span>{label}</span>
          {label !== '' ? <ExtraText>*</ExtraText> : <></>}
        </Label>
      ) : null}
      <Autocomplete
        className={`google-autocomplete${
          props.inputType === 'secondary'
            ? ' google-autocomplete--on-brand'
            : ''
        }`}
        defaultValue={
          value?.address
            ? `${value?.address} ${value?.city} ${value?.zipCode} ${value?.country}`
            : ''
        }
        language={languageService.getCurrentLanguage()}
        options={
          type === 'address'
            ? {
                types: ['address'],
                fields: ['address_components', 'adr_address', 'geometry'],
              }
            : {}
        }
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          checkIfValueEmpty(e.target.value)
        }
        style={
          customStyle
            ? customStyle
            : {
                width: '100%',
                height: props.inputType === 'primary' ? '46px' : '44px',
                marginTop: props.inputType === 'primary' ? '6px' : undefined,
                padding: props.inputType === 'primary' ? '5px 14px' : '0 20px',
                borderRadius: '14px',
                border: `1px solid ${
                  props.inputType === 'primary'
                    ? colors.border
                    : 'rgba(47, 48, 113, 0.16)'
                }`,
                background:
                  props.inputType === 'primary'
                    ? colors.surface
                    : 'linear-gradient(135deg, #2F3071 0%, #493F83 55%, #7468D6 100%)',
                color:
                  props.inputType === 'secondary'
                    ? colors.neutral000
                    : colors.primary500,
                textAlign: props.inputType === 'primary' ? 'left' : 'center',
                boxShadow:
                  props.inputType === 'secondary'
                    ? '0 10px 26px rgba(47, 48, 113, 0.28)'
                    : undefined,
              }
        }
        // @ts-ignore
        textInputProps={{
          placeholderTextColor: colors.neutral100,
          opacity: 1,
        }}
        placeholder={placeHolder ? placeHolder : 'Ort'}
        apiKey={env.NEXT_PUBLIC_GOOGLE_API_KEY}
        onPlaceSelected={(place) => {
          if (!place) {
            return;
          }
          const finalPlace: Place = {
            address: '',
            zipCode: '',
            countryShortName: '',
            country: '',
            city: '',
            latitude: place.geometry
              ? place.geometry.location.lat().toString()
              : null,
            longitude: place.geometry
              ? place.geometry.location.lng().toString()
              : null,
          };
          if (place.address_components) {
            const streetNumberIndex = place.address_components.findIndex(
              (obj: {types: string | string[]}) =>
                obj.types.includes('street_number')
            );
            const routeIndex = place.address_components.findIndex(
              (obj: {types: string | string[]}) => obj.types.includes('route')
            );

            if (streetNumberIndex !== -1 && routeIndex !== -1) {
              const streetNumberObject = place.address_components.splice(
                streetNumberIndex,
                1
              )[0];
              place.address_components.splice(
                routeIndex + 1,
                0,
                streetNumberObject
              );
            }

            place.address_components.forEach(
              (component: {
                types: string | string[];
                long_name: string;
                short_name: string;
              }) => {
                if (component.types.includes('postal_code')) {
                  finalPlace.zipCode = component.long_name;
                }
                if (component.types.includes('locality')) {
                  finalPlace.city = component.long_name;
                }
                if (component.types.includes('country')) {
                  finalPlace.country = component.long_name;
                  finalPlace.countryShortName = component.short_name;
                }
                if (component.types.includes('route')) {
                  finalPlace.address += component.long_name + ' ';
                }
                if (component.types.includes('street_number')) {
                  finalPlace.address += component.long_name + ' ';
                }
                if (!component.types.includes('postal_code')) {
                  finalPlace.zipCode = '0';
                }
              }
            );
          }
          handleNewPlace(finalPlace);
        }}
      />
      <ErrorContainer style={{zIndex: 1}} show={error ? true : false}>
        {error ? (
          <>
            <ErrorText className="text-l12">{error}</ErrorText>
          </>
        ) : (
          ''
        )}
        <ErrorTriangle></ErrorTriangle>
      </ErrorContainer>
    </AutocompleteContainer>
  );
};
